import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// API Definitions search results link to `/definition#<anchor>`, but the API
// definition widgets render after the page loads, so the browser never scrolls to the
// operation. This waits for the operation to render and scrolls it into view.
//
// Anchors:
//   OpenAPI  `#/<tag>/<operationId>`  (Swagger UI deep link; Swagger expands the operation
//            and renders it with id `operations-<tag>-<operationId>`)
//   AsyncAPI `#operation-<action>-<operationId>` (element id used by the AsyncAPI viewer)

const GIVE_UP_AFTER_MS = 15000;
// The widgets keep laying out content above the operation after it first appears,
// which moves it again (or cancels a scroll), so scroll again until it stays in place.
const SCROLL_ATTEMPTS_MS = [100, 500, 1000, 2000, 3500];
const IN_PLACE_PX = 20;
// Stop correcting the position as soon as the user scrolls or navigates themselves.
const USER_EVENTS = ['wheel', 'touchmove', 'keydown', 'mousedown'];

/**
 * `anchor` is the raw URL fragment, still percent-encoded. Swagger segments are
 * decoded one by one after splitting, so an encoded slash inside a tag
 * (`Vehicle%2FV5`) is not mistaken for a separator.
 */
export function findApiOperationElement(
  anchor: string,
  root: ParentNode = document,
): HTMLElement | null {
  if (anchor.startsWith('/')) {
    const [tag, operationId] = anchor
      .slice(1)
      .split('/')
      .map(segment => decodeURIComponent(segment));
    if (!tag || !operationId) {
      return null;
    }
    // Swagger UI CSS-escapes the id attribute, so compare without the escapes.
    const expected = `operations-${tag}-${operationId}`.replace(/\s/g, '_');
    const candidates = root.querySelectorAll<HTMLElement>(
      '[id^="operations-"]',
    );
    return (
      Array.from(candidates).find(
        element => element.id.replace(/\\/g, '') === expected,
      ) ?? null
    );
  }
  const decoded = decodeURIComponent(anchor);
  return (
    Array.from(root.querySelectorAll<HTMLElement>('[id]')).find(
      element => element.id === decoded,
    ) ?? null
  );
}

export const ScrollToApiOperation = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash || hash.length < 2) {
      return undefined;
    }
    const anchor = hash.slice(1);
    const scrollTimers: ReturnType<typeof setTimeout>[] = [];
    let observer: MutationObserver | undefined;
    let giveUpTimer: ReturnType<typeof setTimeout> | undefined;

    const scrollWhenFound = () => {
      if (!findApiOperationElement(anchor)) {
        return false;
      }
      for (const delay of SCROLL_ATTEMPTS_MS) {
        scrollTimers.push(
          setTimeout(() => {
            // Look the element up again: the AsyncAPI viewer re-renders and replaces it.
            const element = findApiOperationElement(anchor);
            if (
              element &&
              (delay === SCROLL_ATTEMPTS_MS[0] ||
                Math.abs(element.getBoundingClientRect().top) > IN_PLACE_PX)
            ) {
              element.scrollIntoView({ block: 'start' });
            }
          }, delay),
        );
      }
      return true;
    };

    // Cancels everything still pending: scroll retries, the wait for the element and
    // the give-up timer. Used on cleanup and when the user takes over scrolling.
    const cancel = () => {
      scrollTimers.forEach(clearTimeout);
      observer?.disconnect();
      clearTimeout(giveUpTimer);
      USER_EVENTS.forEach(event =>
        window.removeEventListener(event, cancel, true),
      );
    };

    USER_EVENTS.forEach(event => window.addEventListener(event, cancel, true));
    if (!scrollWhenFound()) {
      observer = new MutationObserver(() => {
        if (scrollWhenFound()) {
          observer?.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      giveUpTimer = setTimeout(() => observer?.disconnect(), GIVE_UP_AFTER_MS);
    }

    return cancel;
  }, [hash]);

  return null;
};
